/**
 * Card slot derivation for the dashboard ribbon.
 *
 * Extracted from App.tsx so the slot *decisions* can be exercised by a
 * standalone test. The ribbon itself has no harness — there is no React test
 * runner in the monorepo — so keeping the branching in a pure module is the
 * only way to cover it at all.
 */

import type { CardKind, Urgency } from '../components/CountdownCard.tsx';
import type { FollowingLive } from './following.ts';

export interface CardSpec {
  empty: boolean;
  kind?: CardKind;
  label?: string;
  clipName?: string;
  seconds?: number;
  scheduledAt?: Date;
  urgency?: Urgency;
  emptyLabel?: string;
}

/** Left slot, when no live is on air and none is announced. */
export const EMPTY_LEFT_NO_LIVE = 'Aucun direct en cours';

/**
 * Right slot, when the OTAV reports no upcoming live at all.
 *
 * Truthful: the `next_live_*` fields are emitted only when the current
 * playlist still holds a future `clip_type: 4` item. Their absence means
 * there is genuinely no live left to come.
 */
export const EMPTY_RIGHT_NO_NEXT = 'Aucun direct à venir';

/**
 * Right slot, when a live IS announced — and therefore displayed in the LEFT
 * slot — but nothing is known about what follows it.
 *
 * The OTAV WebSocket exposes exactly ONE upcoming live. It says nothing about
 * the one after. Reusing EMPTY_RIGHT_NO_NEXT here would make the right card
 * assert "no upcoming live" while the left card counts down to one: the two
 * cards would contradict each other on screen, in the same second.
 */
export const EMPTY_RIGHT_UNKNOWN = 'Direct suivant non communiqué';

/**
 * Right slot, when the collector walked the whole playlist past the announced
 * live and found nothing after it. Asserted ONLY on the collector's positive
 * marker (`following_live_known`), never inferred from missing fields.
 */
export const EMPTY_RIGHT_NO_MORE = 'Aucun autre direct dans cette playlist';

/** Pill label of the N+2 info card. */
export const FOLLOWING_LABEL = 'Direct suivant';

/**
 * Left slot = the most urgent live card:
 *   - RENDU while a live is on air (real or synthetic),
 *   - else PRISE if a next live is announced,
 *   - else empty.
 */
export function deriveLeftSlot(
  presenterLive: boolean,
  renduSpec: CardSpec,
  priseSpec: CardSpec | null,
): CardSpec {
  if (presenterLive) return renduSpec;
  return priseSpec ?? { empty: true, emptyLabel: EMPTY_LEFT_NO_LIVE };
}

/**
 * Right slot = what is queued behind the left card. A PRISE when a live is on
 * air AND a next one is counting down; otherwise it answers "what comes after
 * the live on the left?", with only as much as is actually known:
 *
 *   | on air | next live counting | next live in data | right slot             |
 *   |--------|--------------------|-------------------|------------------------|
 *   | yes    | yes                | yes               | PRISE                  |
 *   | —      | —                  | NO                | none to come (true)    |
 *   | no     | yes                | yes               | ← following (state 3)  |
 *   | yes    | no                 | yes               | ← following (state 2b) |
 *
 * "following" is the N+2 info card, "no other live in this playlist", or
 * "not communicated" — see `FollowingLive`.
 *
 * State 2b is the ~7 s window after a live starts on the presenter's side,
 * once the PRISE card has expired but before the OTAV switches over: the data
 * still names that live as "next", so its following live is exactly the one
 * after the live being presented. The slot used to claim "no live to come"
 * there, as state 3 did before 2026-09-23.
 *
 * The assertive "none to come" is only ever read from the OTAV itself: no
 * `next_live_*` in the data means the current playlist holds no further live.
 */
export function deriveRightSlot(a: {
  presenterLive: boolean;
  priseSpec: CardSpec | null;
  /** The anchor names a next live — even if its countdown card has expired. */
  nextLiveAnnounced: boolean;
  following: FollowingLive;
}): CardSpec {
  if (a.presenterLive && a.priseSpec) return a.priseSpec;
  // A counting-down PRISE implies an announced next live, whatever the flag.
  const announced = a.nextLiveAnnounced || a.priseSpec !== null;
  if (!announced) return { empty: true, emptyLabel: EMPTY_RIGHT_NO_NEXT };
  switch (a.following.kind) {
    case 'live':
      return {
        empty: false,
        kind: 'info',
        label: FOLLOWING_LABEL,
        clipName: a.following.displayName,
        scheduledAt: a.following.at,
      };
    case 'none':
      return { empty: true, emptyLabel: EMPTY_RIGHT_NO_MORE };
    case 'unknown':
      return { empty: true, emptyLabel: EMPTY_RIGHT_UNKNOWN };
  }
}
